// Statement parsing -- ported from the original AFITEXP1/index.html prototype's
// Khatabook/CSV import engine (parsePDFStatement, parseKhatabookLines,
// inferTypeFromKhatabookLine, cleanDescription, parseStatementDate,
// parseCSVOrText, splitCSV, num, parseFlexibleDate, autoCategory). The parsing
// logic itself was already correct and is reused as-is; only the surrounding
// glue changed (no DOM, no localStorage, returns plain row objects, field
// names match public.transactions).
//
// CSV parsing is additively extended (not rewritten) to also recognize
// explicit Type / Category / Payment Mode columns, on top of the original
// debit/credit-column behavior.

let pdfjsLoadPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load PDF reader library'));
    document.head.appendChild(s);
  });
}

async function ensurePdfJs() {
  if (window.pdfjsLib) return window.pdfjsLib;
  if (!pdfjsLoadPromise) {
    pdfjsLoadPromise = loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js');
  }
  await pdfjsLoadPromise;
  if (!window.pdfjsLib) throw new Error('PDF reader library could not load. Check your internet connection and try again.');
  window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  return window.pdfjsLib;
}

export async function parsePDFStatement(file) {
  const pdfjs = await ensurePdfJs();
  const buf = await file.arrayBuffer();
  let pdf;
  try {
    pdf = await pdfjs.getDocument({ data: buf }).promise;
  } catch (err) {
    throw new Error('Could not read this PDF: ' + (err.message || String(err)));
  }
  const all = [];
  for (let n = 1; n <= pdf.numPages; n++) {
    const page = await pdf.getPage(n);
    const content = await page.getTextContent();
    const items = content.items.map((x) => ({ str: x.str, x: x.transform[4], y: x.transform[5] }));
    // Reconstruct visual lines from PDF text positions.
    const groups = [];
    items.sort((a, b) => b.y - a.y || a.x - b.x);
    for (const it of items) {
      let g = groups.find((z) => Math.abs(z.y - it.y) < 2.5);
      if (!g) { g = { y: it.y, items: [] }; groups.push(g); }
      g.items.push(it);
    }
    all.push(groups.sort((a, b) => b.y - a.y).map((g) => g.items.sort((a, b) => a.x - b.x).map((i) => i.str).join(' ').replace(/\s+/g, ' ').trim()).filter(Boolean));
  }
  const lines = all.flat();
  const rows = parseKhatabookLines(lines);
  if (!rows.length) {
    throw new Error('No transactions were found in this PDF. It may not be a supported statement format.');
  }
  return rows;
}

export function parseKhatabookLines(lines) {
  const out = [];
  const dateRe = /^(\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)(?:\s+\d{2,4})?)/i;
  const fullDateRe = /^(\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{2,4})/i;
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].trim();
    if (!dateRe.test(line) && !fullDateRe.test(line)) continue;
    if (/opening balance/i.test(line)) continue;
    const m = line.match(dateRe) || line.match(fullDateRe);
    if (!m) continue;
    const rest = line.slice(m[0].length).trim();
    const nums = [...rest.matchAll(/(?:₹\s*)?([\d,]+(?:\.\d{1,2})?)/g)].map((x) => Number(x[1].replace(/,/g, '')));
    if (!nums.length) continue;

    // In Khatabook, the last numeric value is normally the running balance.
    // The first numeric value after the description is the actual transaction amount.
    const amount = nums[0];
    const desc = cleanDescription(rest, nums);
    const type = inferTypeFromKhatabookLine(desc);
    const entry_date = parseStatementDate(m[0]);
    if (entry_date && amount > 0 && desc) {
      out.push({ entry_date, type, amount, description: desc, category: autoCategory(desc), payment_mode: 'Imported' });
    }
  }
  return out;
}

function inferTypeFromKhatabookLine(desc) {
  // Khatabook's running-balance Cr/Dr marker cannot be used alone to classify
  // the transaction. Classify from the transaction description instead.
  const s = String(desc || '').toLowerCase();
  if (/from\s+client|client\s+payment|payment\s+received|received\s+from|advance\s+received|income|collection/.test(s)) return 'income';
  return 'expense';
}

function cleanDescription(rest, nums) {
  let s = rest;
  s = s.replace(/\b(?:Dr|Cr)\b/gi, ' ');
  s = s.replace(/(?:₹\s*)?[\d,]+(?:\.\d{1,2})?/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  return s || 'Imported transaction';
}

function parseStatementDate(s) {
  const months = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
    january: 0, february: 1, march: 2, april: 3, june: 5, july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
  };
  const p = s.trim().split(/\s+/);
  if (p.length < 2) return '';
  const d = parseInt(p[0], 10);
  const mon = months[p[1].toLowerCase()];
  let y = p[2] ? parseInt(p[2], 10) : new Date().getFullYear();
  if (y < 100) y += 2000;
  if (Number.isNaN(d) || mon === undefined) return '';
  return `${y}-${String(mon + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function splitCSV(line) {
  const r = [];
  let cur = '', q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { q = !q; continue; }
    if (ch === ',' && !q) { r.push(cur); cur = ''; } else cur += ch;
  }
  r.push(cur);
  return r;
}

function num(v) {
  return Number(String(v || '').replace(/[₹,\s]/g, '')) || 0;
}

function parseFlexibleDate(v) {
  if (!v) return '';
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return parseStatementDate(v);
}

export function parseCSVOrText(text) {
  const lines = text.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
  const out = [];
  const hasHeader = lines[0] && /date/i.test(lines[0]) && /description|details|particular/i.test(lines[0]);

  if (hasHeader) {
    const headers = splitCSV(lines[0]).map((x) => x.toLowerCase().trim());
    const get = (c, names) => {
      const i = headers.findIndex((h) => names.some((n) => h.includes(n)));
      return i >= 0 ? c[i] : '';
    };
    for (const line of lines.slice(1)) {
      const c = splitCSV(line);
      if (c.length < 2) continue;
      const entry_date = parseFlexibleDate(get(c, ['date']));
      const description = get(c, ['description', 'details', 'particular']);
      const typeCol = get(c, ['type']);
      const categoryCol = get(c, ['category']);
      const paymentCol = get(c, ['payment mode', 'payment', 'mode']);
      const amountCol = num(get(c, ['amount']));
      const debit = num(get(c, ['debit', 'expense']));
      const credit = num(get(c, ['credit', 'income']));

      let type, amount;
      if (typeCol) {
        type = /income/i.test(typeCol) ? 'income' : 'expense';
        amount = amountCol || debit || credit;
      } else if (amountCol) {
        type = inferTypeFromKhatabookLine(description);
        amount = amountCol;
      } else {
        type = credit ? 'income' : 'expense';
        amount = credit || debit;
      }

      // Keep the row even if amount didn't parse to a positive number -- an
      // unparseable/zero amount must surface as an INVALID row for the user to
      // see, not vanish silently. Downstream validation (validateRow) is what
      // actually rejects it.
      if (entry_date && description) {
        out.push({
          entry_date,
          type,
          amount,
          description,
          category: categoryCol || autoCategory(description),
          payment_mode: paymentCol || 'Imported'
        });
      }
    }
  } else {
    // No recognizable header -- try the Khatabook line parser on plain text.
    return parseKhatabookLines(lines);
  }
  return out;
}

/**
 * XLSX parsing (Phase 4B). Reuses the same low-level helpers CSV parsing uses
 * (num, parseFlexibleDate, autoCategory, inferTypeFromKhatabookLine) rather than
 * calling into parseCSVOrText directly, so the working CSV parser's own function
 * body stays untouched. Column matching mirrors CSV's (Date / Description-
 * Details-Particular / Type / Category / Payment Mode-Payment-Mode / Notes-
 * Remark / Amount / Debit-Expense / Credit-Income), plus Notes support.
 *
 * Unlike parseCSVOrText, this keeps every non-fully-blank row (even one missing
 * date/description/amount) so nothing is silently discarded -- validateRow()
 * downstream is what actually rejects a bad row, with a visible reason.
 */
export async function parseXLSXStatement(file) {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  let workbook;
  try {
    workbook = XLSX.read(buf, { type: 'array' });
  } catch (err) {
    throw new Error('Could not read this XLSX file: ' + (err.message || String(err)));
  }
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error('This XLSX file has no sheets.');
  const records = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '', raw: false });
  if (!records.length) throw new Error('No rows were found in this XLSX file.');

  const out = [];
  for (const record of records) {
    const lower = {};
    for (const k of Object.keys(record)) lower[k.toLowerCase().trim()] = record[k];
    const get = (names) => {
      const key = Object.keys(lower).find((h) => names.some((n) => h.includes(n)));
      return key !== undefined ? String(lower[key] ?? '').trim() : '';
    };

    const entry_date = parseFlexibleDate(get(['date']));
    const description = get(['description', 'details', 'particular']);
    const typeCol = get(['type']);
    const categoryCol = get(['category']);
    const paymentCol = get(['payment mode', 'payment', 'mode']);
    const notesCol = get(['notes', 'remark']);
    const amountCol = num(get(['amount']));
    const debit = num(get(['debit', 'expense']));
    const credit = num(get(['credit', 'income']));

    let type = '';
    let amount = amountCol || debit || credit;
    if (typeCol) {
      type = /income/i.test(typeCol) ? 'income' : 'expense';
    } else if (credit || debit) {
      type = credit ? 'income' : 'expense';
    } else if (amountCol) {
      type = inferTypeFromKhatabookLine(description);
    }

    // Skip only a row with literally nothing in it (spreadsheet padding) --
    // every other row is kept and classified downstream, never silently dropped.
    if (!entry_date && !description && !amount && !typeCol) continue;

    out.push({
      entry_date,
      type,
      amount,
      description,
      category: categoryCol || (description ? autoCategory(description) : ''),
      payment_mode: paymentCol || 'Imported',
      notes: notesCol || null
    });
  }

  if (!out.length) {
    throw new Error('No usable rows were found in this XLSX file.');
  }
  return out;
}

export function autoCategory(desc) {
  const s = String(desc || '').toLowerCase();
  if (/salary|labour|labor|worker|wage|settlement/.test(s)) return 'Labour';
  if (/transport|porter|auto|lorry|vehicle|freight/.test(s)) return 'Transportation';
  if (/load|unload/.test(s)) return 'Loading / Unloading';
  if (/bag|gypsum|hd mr|cement|material|wire|tube light/.test(s)) return 'Materials';
  if (/polish|finish/.test(s)) return 'Finishing';
  return 'Other';
}
